/** Programma di audit compilato nel tool (AUD-F2.14b): modulo dal modello, versioni, anteprima, emissione del PDF, invio. */
import { useEffect, useMemo, useState } from "react";
import { Alert, Button, Card, Checkbox, DatePicker, Input, Popconfirm, Radio, Segmented, Select, Space, Spin, Tag, Typography } from "antd";
import { DeleteOutlined, EyeOutlined, FilePdfOutlined, MailOutlined, PlusOutlined, SaveOutlined, UndoOutlined } from "@ant-design/icons";
import { useTranslation } from "react-i18next";
import { useQueryClient } from "@tanstack/react-query";
import dayjs from "dayjs";
import type { AuditDetailDto, ProgrammeFieldDef, ProgrammeValue, ProgrammeValues } from "@audit-plan/shared";
import { ApiError } from "../../api/client";
import { invalidateAudit } from "../../api/audits";
import { issueProgrammeDoc, programmePreviewUrl, saveProgrammeDoc, sendProgrammeDoc, sendProgrammeTest, useProgrammeDoc } from "../../api/programme";
import { useNotify } from "./useNotify";
import { fmtD, fmtDT } from "../pills";
import { Sensitive, SensitiveSelect } from "../Sensitive";

type Lang = "it" | "en";
const lbl = (x: { it: string; en?: string }, en: boolean) => (en && x.en ? x.en : x.it);
/** Campi del modello con dati di contatto del fornitore (referente, indirizzo del sito): marcati data-sensitive. */
const isSensitiveField = (f: ProgrammeFieldDef) =>
  f.prefill === "supplier.contact" || f.prefill === "factory.address" || /contact|address|email|phone|referent|indirizz|telefon/i.test(f.key);

function FieldInput({ f, value, onChange, disabled, en }: { f: ProgrammeFieldDef; value: ProgrammeValue | undefined; onChange: (v: ProgrammeValue) => void; disabled: boolean; en: boolean }) {
  const { t } = useTranslation();
  const str = typeof value === "string" ? value : "";
  switch (f.type) {
    case "textarea":
      return <Input.TextArea value={str} onChange={(e) => onChange(e.target.value)} autoSize={{ minRows: 2, maxRows: 8 }} disabled={disabled} />;
    case "date":
      return <DatePicker value={str ? dayjs(str) : null} onChange={(d) => onChange(d ? d.format("YYYY-MM-DD") : "")} format="DD/MM/YYYY" disabled={disabled} />;
    case "choice":
      return (
        <Radio.Group value={str || undefined} onChange={(e) => onChange(e.target.value as string)} disabled={disabled}>
          <Space wrap>{(f.options ?? []).map((o) => <Radio key={o.value} value={o.value}>{lbl(o.label, en)}</Radio>)}</Space>
        </Radio.Group>
      );
    case "multi":
      return (
        <Checkbox.Group value={Array.isArray(value) ? (value as string[]) : []} onChange={(v) => onChange(v as string[])} disabled={disabled}>
          <Space direction="vertical" size={2}>{(f.options ?? []).map((o) => <Checkbox key={o.value} value={o.value}>{lbl(o.label, en)}</Checkbox>)}</Space>
        </Checkbox.Group>
      );
    case "table": {
      const cols = f.columns ?? [];
      const rows = Array.isArray(value) ? (value as Record<string, string>[]) : [];
      const set = (i: number, k: string, v: string) => onChange(rows.map((r, j) => (j === i ? { ...r, [k]: v } : r)));
      return (
        <div style={{ overflowX: "auto" }}>
          <table style={{ borderCollapse: "collapse", width: "100%", minWidth: cols.length * 130 }}>
            <thead>
              <tr>
                {cols.map((c) => <th key={c.key} style={{ textAlign: "left", fontWeight: 600, fontSize: 12.5, padding: "4px 6px", opacity: 0.75 }}>{lbl(c.label, en)}</th>)}
                <th style={{ width: 36 }} />
              </tr>
            </thead>
            <tbody>
              {rows.map((r, i) => (
                <tr key={i}>
                  {cols.map((c) => (
                    <td key={c.key} style={{ padding: 2 }}>
                      <Input size="small" value={r[c.key] ?? ""} onChange={(e) => set(i, c.key, e.target.value)} disabled={disabled} />
                    </td>
                  ))}
                  <td>{!disabled && <Button size="small" type="text" icon={<DeleteOutlined />} aria-label="remove" onClick={() => onChange(rows.filter((_, j) => j !== i))} />}</td>
                </tr>
              ))}
            </tbody>
          </table>
          {!disabled && <Button size="small" type="dashed" icon={<PlusOutlined />} style={{ marginTop: 6 }} onClick={() => onChange([...rows, {}])}>{t("pd_addRow")}</Button>}
        </div>
      );
    }
    default:
      return <Input value={str} onChange={(e) => onChange(e.target.value)} disabled={disabled} />;
  }
}

export function ProgrammeDocPanel({ audit: a }: { audit: AuditDetailDto }) {
  const { t, i18n } = useTranslation();
  const en = i18n.language === "en";
  const qc = useQueryClient();
  const n = useNotify();
  const q = useProgrammeDoc(a.id);
  const doc = q.data;
  const [values, setValues] = useState<ProgrammeValues | null>(null);
  const [dirty, setDirty] = useState(false);
  const [lang, setLang] = useState<Lang>(en ? "en" : "it");
  const [to, setTo] = useState<string[]>([]);
  const [busy, setBusy] = useState<string | null>(null);

  useEffect(() => {
    if (doc && !dirty) setValues(doc.values);
  }, [doc, dirty]);
  useEffect(() => {
    // sensitive-ok: stato della SensitiveSelect
    if (doc && !to.length && doc.recipients.length) setTo([doc.recipients[0]!.email]);
  }, [doc]); // eslint-disable-line react-hooks/exhaustive-deps

  // sensitive-ok: valore delle opzioni della SensitiveSelect (tendina mascherata)
  const recipientOpts = useMemo(() => (doc?.recipients ?? []).map((r) => ({ value: r.email, label: <Sensitive>{r.name && r.name !== r.email ? `${r.name} <${r.email}>` : r.email}</Sensitive> })), [doc]);
  if (q.isLoading || !doc || !values) return <Spin style={{ display: "block", margin: 32 }} />;
  if (q.error) return <Alert type="error" showIcon message={q.error instanceof ApiError ? q.error.message : String(q.error)} />;

  const can = doc.allowed;
  const sections = doc.model.sections;
  const setField = (s: string, f: string, v: ProgrammeValue) => {
    setValues((p) => ({ ...p!, [s]: { ...p![s], [f]: v } }));
    setDirty(true);
  };
  const run = async <R,>(key: string, fn: () => Promise<R>, ok?: string | ((r: R) => string)) => {
    setBusy(key);
    try {
      const r = await fn();
      void qc.invalidateQueries({ queryKey: ["programme-doc", a.id] });
      invalidateAudit(qc, a.id);
      n.ok(typeof ok === "function" ? ok(r) : ok);
    } catch (e) {
      n.fail(e);
    } finally {
      setBusy(null);
    }
  };
  const issued = doc.issued;
  const emails = to.map((x) => x.trim()).filter(Boolean);
  // sensitive-ok: ricerca per l'invio, non si mostra
  const nameOf = (e: string) => doc.recipients.find((r) => r.email === e)?.name ?? "";

  return (
    <div style={{ display: "grid", gap: 16 }}>
      <Alert type="info" showIcon message={t("pd_intro")} />
      <Card size="small">
        <Space wrap size={[12, 6]}>
          <Tag color={doc.current ? "blue" : "default"}>{doc.current ? t("pd_saved", { v: doc.current.v, d: fmtDT(doc.current.savedAt) }) : t("pd_notSaved")}</Tag>
          <Tag color={issued ? "gold" : "default"}>{issued ? t("pd_issued", { v: issued.v, d: fmtDT(issued.at) }) : t("pd_notIssued")}</Tag>
          {doc.sent && (
            <Tag color="green" data-sensitive>
              {doc.sent.via === "mail" ? t("pd_sentMail", { d: fmtD(doc.sent.at), to: doc.sent.to, v: doc.sent.version }) : t("pd_sentManual", { d: fmtD(doc.sent.at), to: doc.sent.to })}
            </Tag>
          )}
          {dirty && <Tag color="orange">{t("pd_dirty")}</Tag>}
          <Typography.Text type="secondary" style={{ fontSize: 12 }}>{doc.model.title} · v{doc.model.v}</Typography.Text>
        </Space>
      </Card>

      {sections.map((sec) => (
        <Card key={sec.key} size="small" title={lbl(sec.title, en)}>
          <div style={{ display: "grid", gap: 12 }}>
            {sec.fields.map((f) => (
              <div key={f.key} style={{ display: "grid", gridTemplateColumns: f.type === "table" ? "1fr" : "minmax(160px, 260px) 1fr", gap: "4px 16px", alignItems: "start" }}>
                {!(f.type === "table" && sec.fields.length === 1) && (
                  <div style={{ fontSize: 13, paddingTop: 5, opacity: 0.85 }}>
                    {lbl(f.label, en)}
                    {f.help && <div style={{ fontSize: 11.5, opacity: 0.7 }}>{lbl(f.help, en)}</div>}
                  </div>
                )}
                {isSensitiveField(f) ? (
                  <Sensitive block>
                    <FieldInput f={f} value={values[sec.key]?.[f.key]} onChange={(v) => setField(sec.key, f.key, v)} disabled={!can} en={en} />
                  </Sensitive>
                ) : (
                  <FieldInput f={f} value={values[sec.key]?.[f.key]} onChange={(v) => setField(sec.key, f.key, v)} disabled={!can} en={en} />
                )}
              </div>
            ))}
          </div>
        </Card>
      ))}

      {can && (
        <Card size="small" style={{ position: "sticky", bottom: 8, zIndex: 5, boxShadow: dirty ? "0 4px 16px rgba(0,0,0,.12)" : undefined }}>
          <Space wrap>
            <Button type="primary" icon={<SaveOutlined />} disabled={!dirty} loading={busy === "save"} onClick={() => void run("save", async () => { await saveProgrammeDoc(a.id, values); setDirty(false); })}>
              {t("pd_save")}
            </Button>
            <Popconfirm title={t("pd_resetQ")} onConfirm={() => { setValues(doc.prefill); setDirty(true); }} okText={t("confirm")} cancelText={t("cancel")}>
              <Button icon={<UndoOutlined />}>{t("pd_reset")}</Button>
            </Popconfirm>
            {doc.versions.length > 0 && (
              <Typography.Text type="secondary" style={{ fontSize: 12 }}>
                {t("pd_versions")}: {doc.versions.slice(0, 5).map((v) => t("pd_versionRow", { v: v.v, d: fmtDT(v.savedAt), m: v.modelV })).join(" · ")}
              </Typography.Text>
            )}
          </Space>
        </Card>
      )}

      <Card size="small" title={`PDF · ${t("pd_send_step")}`}>
        <div style={{ display: "grid", gap: 12 }}>
          {doc.mailMode !== "all" && <Alert type="warning" showIcon message={doc.mailMode === "tests" ? t("pd_testsMode") : t("pd_offMode")} />}
          <Space wrap align="center">
            <span style={{ opacity: 0.8 }}>{t("pd_lang")}</span>
            <Segmented value={lang} onChange={(v) => setLang(v as Lang)} options={[{ value: "it", label: "IT" }, { value: "en", label: "EN" }]} />
            <Button icon={<EyeOutlined />} href={programmePreviewUrl(a.id, lang)} target="_blank" disabled={dirty} title={dirty ? t("pd_saveFirst") : undefined}>{t("pd_preview")}</Button>
            {can && (
              <Button icon={<FilePdfOutlined />} disabled={dirty} loading={busy === "issue"} title={dirty ? t("pd_saveFirst") : t("pd_issueHint")} onClick={() => void run("issue", () => issueProgrammeDoc(a.id, lang))}>
                {t("pd_issue")}
              </Button>
            )}
          </Space>
          {can && (
            <Space wrap align="start">
              <SensitiveSelect<string[]>
                mode="tags"
                style={{ minWidth: 360, maxWidth: "100%" }}
                value={to}
                onChange={(v) => setTo(v)}
                options={recipientOpts}
                placeholder={t("pd_toPh")}
                tokenSeparators={[",", ";", " "]}
                aria-label={t("pd_to")}
              />
              <Popconfirm
                title={t("pd_sendQ", { v: issued?.v ?? 0, n: emails.length })}
                disabled={!issued || dirty || !emails.length}
                onConfirm={() =>
                  void run(
                    "send",
                    () => sendProgrammeDoc(a.id, lang, emails.map((e) => ({ name: nameOf(e), email: e }))),
                    t("pd_sentOk", { to: emails.join(", ") }),
                  )
                }
                okText={t("confirm")}
                cancelText={t("cancel")}
              >
                <Button type="primary" icon={<MailOutlined />} disabled={!issued || dirty || !emails.length} loading={busy === "send"} title={!issued ? t("pd_needIssue") : undefined}>
                  {t("pd_send")}
                </Button>
              </Popconfirm>
              <Button disabled={!issued || dirty} loading={busy === "test"} onClick={() => void run("test", () => sendProgrammeTest(a.id, lang), (r) => t("pd_testSent", { to: r.to }))}>
                {t("pd_sendTest")}
              </Button>
            </Space>
          )}
        </div>
      </Card>
    </div>
  );
}
